/**
 * What a scanned law's text carries that is not the law: the gazette's running header, repeated on
 * every page, plus page numbers and underscore rules (MAR-948 wave 3).
 *
 * The running header belongs to the JURISDICTION's official print, so it is keyed by jurisdiction.
 * `cleanSectionText` used to strip El Salvador's Asamblea header from every norm, and
 * `lawPdfTarget.ts` kept a second copy of the same list for the PDF search text; on a German or
 * Guatemalan norm a line reading that way would be text, not an artefact. Both read this table now.
 * A jurisdiction with no entry gets the generic clean-up only. Add a jurisdiction's header here,
 * never in a caller (`tests/unit/legalDisplayGuard.test.ts`).
 */

export interface RunningHeaders {
  /** Matched anywhere in a section's text, with surrounding `**` markers and newlines. */
  phrases: readonly RegExp[]
  /** Matched against one whole line of PDF text (the viewer's text layer splits the header). */
  lines: readonly RegExp[]
}

const NONE: RunningHeaders = { phrases: [], lines: [] }

const RUNNING_HEADERS: Readonly<Record<string, RunningHeaders>> = {
  // The Asamblea Legislativa's "Índice Legislativo" print.
  SV: {
    phrases: [
      /[ÍI]NDICE LEGISLATIVO/,
      /ASAMBLEA LEGISLATIVA\s*[-–—]\s*REP[ÚU]BLICA DE EL SALVADOR/,
    ],
    lines: [
      /^[ÍI]NDICE LEGISLATIVO$/i,
      /^ASAMBLEA LEGISLATIVA\b.*$/i,
      /^REP[ÚU]BLICA DE EL SALVADOR\b.*$/i,
    ],
  },
}

/** The running headers of `jurisdiction`'s print; none for an unknown or missing jurisdiction. */
export function runningHeaders(jurisdiction: string | null | undefined): RunningHeaders {
  const key = String(jurisdiction || '').trim().toUpperCase()
  return RUNNING_HEADERS[key] ?? NONE
}

/** A PDF text line that is a page number or an underscore rule, in any jurisdiction. */
export const GENERIC_ARTIFACT_LINE = /^(?:_+\s*\d*|\d{1,4})$/
